import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchEntityExport,
  fetchEntityPage,
  fetchEntitySummary,
  type EntityQuery,
  type EntitySummary,
} from '@/lib/entity-api';
import type { GenericTableServerMode } from '@/styles/GenericTable';

/**
 * THE state behind every server-driven list (table or cards) in KrewUp: one `EntityQuery`
 * (project + search + filters + sort) -> the API -> total / has_more -> rows. The module only supplies
 * its entity type, how a row becomes an item, and (optionally) how a page is enriched; nothing about
 * searching, filtering, sorting or paging is re-implemented per screen.
 *
 *  - ANY change of the query (project, search, filters, sort) restarts from the first page, empties the
 *    list and discards answers still in flight for the previous query: results of one query never mix
 *    into another, and a slow old answer can never overwrite a newer one.
 *  - `mode: 'pages'` shows one page at a time (paginator); `mode: 'append'` accumulates pages
 *    (load more / infinite scroll). Both ask the API for exactly one page of `pageSize` rows.
 *  - `exportRows` and `summary` use the SAME query, so the export and the KPIs always describe the
 *    records the table is looking at -- all of them, not just the loaded pages.
 *  - `tableProps` spreads straight into <GenericTable>.
 */
export interface EntityListSort {
  columnKey: string;
  direction: 'asc' | 'desc';
  sortKey?: string;
}

export interface UseEntityListOptions<TRow, TItem> {
  entityType: string;
  /** The Global Project Selector value ('all' / undefined = no project filter). */
  projectId?: string;
  /** Wait until the user / company is known. */
  enabled?: boolean;
  mode?: 'pages' | 'append';
  pageSize?: number;
  includeCounts?: boolean;
  includeCreator?: boolean;
  includeRollups?: boolean;
  /** Filters owned by the screen (e.g. a status dropdown); sent to the API as-is. */
  filters?: EntityQuery['filters'];
  /** Sort to start with (a key the entity declares sortable). Without one the server shows newest first. */
  defaultSort?: EntityListSort;
  searchDebounceMs?: number;
  mapRow: (row: TRow) => TItem;
  /** Adds data that lives outside the entity row to ONE page (e.g. its attachments) -- only the visible page is enriched. */
  enrichPage?: (rows: TRow[], items: TItem[]) => Promise<TItem[]>;
  /** Also load KPIs (needs `summary` in the entity's entity-config). */
  summary?: boolean;
  getId?: (item: TItem) => string;
  /**
   * For screens whose page comes from their own endpoint (Selections: the aggregate read with options and KPIs). It
   * receives the SAME EntityQuery and must page / search / sort on the server; `extra` (e.g. KPIs computed over the whole
   * matching dataset) is handed back as `pageExtra`.
   */
  fetchPage?: (query: EntityQuery, page: { limit: number; offset: number }) => Promise<{ rows: TRow[]; total: number; hasMore: boolean; extra?: unknown }>;
}

export function useEntityList<TRow extends { id: string }, TItem>(options: UseEntityListOptions<TRow, TItem>) {
  const {
    entityType, projectId, enabled = true, mode = 'pages', pageSize = 10, includeCounts, includeCreator, includeRollups,
    filters, defaultSort, searchDebounceMs = 300, mapRow, enrichPage, summary: wantsSummary,
  } = options;
  const getId = options.getId ?? ((item: TItem) => (item as unknown as { id: string }).id);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<EntityListSort | null>(defaultSort ?? null);
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), searchDebounceMs);
    return () => clearTimeout(timer);
  }, [searchInput, searchDebounceMs]);

  // The query: ONE object feeds pages, export and KPIs. Keyed by content so a new-but-equal object never refetches.
  const filtersKey = JSON.stringify(filters ?? {});
  const projectFilter = projectId && projectId !== 'all' ? projectId : undefined;
  const sortKey = sort ? (sort.sortKey ?? sort.columnKey) : undefined;
  const sortDirection = sort?.direction;
  const query = useMemo<EntityQuery>(
    () => ({
      projectId: projectFilter,
      search: search || undefined,
      filters: JSON.parse(filtersKey),
      sort: sortKey,
      direction: sortKey ? sortDirection : undefined,
    }),
    [projectFilter, search, filtersKey, sortKey, sortDirection]
  );
  const queryKey = JSON.stringify(query);

  const [items, setItems] = useState<TItem[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // True once the first read has answered (even with an error): a screen shows its full-page loader only before that,
  // never while a search / sort / page is loading (that would unmount the search box the user is typing in).
  const [hasLoaded, setHasLoaded] = useState(false);
  // The failure of the last read of the list itself (null while it works): a screen can offer a retry.
  const [error, setError] = useState<string | null>(null);
  const [kpis, setKpis] = useState<EntitySummary | null>(null);
  const [pageState, setPageState] = useState({ key: queryKey, page: 1 });
  const page = pageState.key === queryKey ? pageState.page : 1;

  // Bumped by every query change / reload: an answer for an older request is dropped.
  const generation = useRef(0);
  const loadedIds = useRef<Set<string>>(new Set());
  const serverOffset = useRef(0);
  const latest = useRef({ query, mapRow, enrichPage, getId, fetchPageOverride: options.fetchPage });
  latest.current = { query, mapRow, enrichPage, getId, fetchPageOverride: options.fetchPage };
  const [pageExtra, setPageExtra] = useState<unknown>(undefined);
  const itemsRef = useRef<TItem[]>([]);
  itemsRef.current = items;

  const fetchPage = useCallback(
    async (offset: number): Promise<{ items: TItem[]; total: number; hasMore: boolean; rowCount: number; extra?: unknown } | null> => {
      const gen = generation.current;
      const { query: q, mapRow: map, enrichPage: enrich, fetchPageOverride } = latest.current;
      const result = fetchPageOverride
        ? await fetchPageOverride(q, { limit: pageSize, offset })
        : await fetchEntityPage<TRow>(entityType, q, { limit: pageSize, offset, includeCounts, includeCreator, includeRollups });
      let mapped = result.rows.map(map);
      if (enrich && result.rows.length > 0) {
        try {
          mapped = await enrich(result.rows, mapped);
        } catch (error) {
          console.error(`[useEntityList:${entityType}] could not enrich the page`, error);
        }
      }
      if (gen !== generation.current) return null;
      return { items: mapped, total: result.total, hasMore: result.hasMore, rowCount: result.rows.length, extra: (result as { extra?: unknown }).extra };
    },
    [entityType, pageSize, includeCounts, includeCreator, includeRollups]
  );


  // A new query (or page, in paginator mode) -> a fresh read.
  const wantedOffset = mode === 'pages' ? (page - 1) * pageSize : 0;
  const [reloadTick, setReloadTick] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    generation.current += 1;
    const gen = generation.current;
    loadedIds.current = new Set();
    serverOffset.current = 0;
    if (mode === 'append') setItems([]);
    setIsLoading(true);
    (async () => {
      try {
        const result = await fetchPage(wantedOffset);
        if (!result || gen !== generation.current) return;
        result.items.forEach((item) => loadedIds.current.add(latest.current.getId(item)));
        serverOffset.current = result.rowCount;
        setItems(result.items);
        setTotal(result.total);
        setHasMore(result.hasMore);
        setPageExtra(result.extra);
        setError(null);
      } catch (failure) {
        if (gen !== generation.current) return;
        console.error(`[useEntityList:${entityType}] failed to load`, failure);
        setError(failure instanceof Error ? failure.message : `Failed to load ${entityType}`);
        setItems([]);
        setTotal(0);
        setHasMore(false);
      } finally {
        if (gen === generation.current) { setIsLoading(false); setHasLoaded(true); }
      }
    })();
  }, [enabled, queryKey, wantedOffset, mode, reloadTick, fetchPage, entityType]);

  // KPIs follow the QUERY (not the page): same project / search / filters, over every matching record.
  const kpiGeneration = useRef(0);
  useEffect(() => {
    if (!enabled || !wantsSummary) return;
    const gen = ++kpiGeneration.current;
    fetchEntitySummary(entityType, latest.current.query)
      .then((result) => { if (gen === kpiGeneration.current) setKpis(result); })
      .catch((error) => console.error(`[useEntityList:${entityType}] could not load the summary`, error));
  }, [enabled, wantsSummary, entityType, queryKey, reloadTick]);

  const loadMore = useCallback(async () => {
    if (mode !== 'append' || isLoading || !hasMore || !enabled) return;
    const gen = generation.current;
    setIsLoading(true);
    try {
      const result = await fetchPage(serverOffset.current);
      if (!result || gen !== generation.current) return;
      const idOf = latest.current.getId;
      const fresh = result.items.filter((item) => !loadedIds.current.has(idOf(item)));
      fresh.forEach((item) => loadedIds.current.add(idOf(item)));
      serverOffset.current += result.rowCount;
      setItems((prev) => [...prev, ...fresh]);
      setTotal(result.total);
      setHasMore(result.hasMore);
      setPageExtra(result.extra);
    } catch (error) {
      if (gen === generation.current) {
        console.error(`[useEntityList:${entityType}] failed to load more`, error);
        setHasMore(false);
      }
    } finally {
      if (gen === generation.current) setIsLoading(false);
    }
  }, [mode, isLoading, hasMore, enabled, fetchPage, entityType]);

  /** Re-reads from the first page (paginator: the current page) -- after a change the list cannot patch locally. */
  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  /**
   * Re-reads what is on screen WITHOUT going back to the top: load-more mode re-reads every page it had loaded (the rows
   * keep showing meanwhile), the paginator re-reads the current page. For changes that stay where they are (selecting an
   * option, saving an edit) and need the server's fresh totals / KPIs.
   */
  const refresh = useCallback(async () => {
    if (mode !== 'append') {
      reload();
      return;
    }
    generation.current += 1;
    const gen = generation.current;
    const target = Math.max(itemsRef.current.length, pageSize);
    const all: TItem[] = [];
    let offset = 0;
    let last: Awaited<ReturnType<typeof fetchPage>> = null;
    try {
      while (offset < target) {
        last = await fetchPage(offset);
        if (!last || gen !== generation.current) return;
        all.push(...last.items);
        offset += last.rowCount;
        if (!last.hasMore || last.rowCount === 0) break;
      }
      if (!last) return;
      const idOf = latest.current.getId;
      loadedIds.current = new Set(all.map(idOf));
      serverOffset.current = offset;
      setItems(all);
      setTotal(last.total);
      setHasMore(last.hasMore);
      setPageExtra(last.extra);
    } catch (error) {
      console.error(`[useEntityList:${entityType}] failed to refresh`, error);
    }
  }, [mode, reload, pageSize, fetchPage, entityType]);

  const setPage = useCallback((next: number) => setPageState({ key: queryKey, page: Math.max(1, next) }), [queryKey]);

  /** Every record of the CURRENT query (not just the loaded pages), read by the backend; `ids` narrows it to a selection. */
  const exportRows = useCallback(
    (ids?: string[]) => fetchEntityExport<Record<string, any>>(entityType, latest.current.query, { ids }),
    [entityType]
  );

  const server: GenericTableServerMode = { total, mode, hasMore, onLoadMore: loadMore };
  const tableProps = {
    data: items,
    loading: isLoading,
    server,
    pageSize,
    page,
    onPageChange: setPage,
    sort: sort ?? undefined,
    onSortChange: (next: EntityListSort | null) => setSort(next),
  };

  return {
    items, total, hasMore, isLoading, hasLoaded, error, page, setPage, loadMore, reload, refresh, pageExtra,
    search, searchInput, setSearchInput, sort, setSort,
    query, queryKey, summary: kpis, exportRows, setItems, tableProps,
  };
}
